/*
  What the cards show, and how the deck draws them. Ported from the app's node-display dialog
  (orgchart.html:1600-1676, :9600-9740).

  A COMMAND, not a lens, and the distinction is the whole reason this modal looks different from the
  filter and grouping ones next to it. The deck exporter reads the same configuration off project
  settings, with no user in the request — a scheduled export has no screen at all — so per-user card
  display would mean the deck showing one reader's choices to everybody. The promise that a slide shows
  what the screen shows only holds if there is one answer per project.

  Which makes it commit-on-change with an inverse, like the assumptions editor: every toggle is its own
  entry in the history and its own undo step, and nothing here holds a local mirror of a committed
  value, so a change another tab makes lands rather than being shadowed.

  Every control is labelled with where it applies. Three groups exist:

   - SCREEN AND DECK — the visibility toggles the card honours and `deck-tree` reads.
   - DECK ONLY — the status colours and the box geometry. The screen draws buckets from its own
     stylesheet palette and lays out through d3-org-chart, neither of which the deck can reproduce; the
     app has the same split and its dialog does not say so (`:1656` claims otherwise).
  The composition bar, the stage-comparison line and the manager rollup ARE offered now — P4i-f taught
  the card to draw them, and `wantStageCounts` is derived from these two boxes rather than pinned false,
  so ticking the stage comparison computes the data as well as asking for it. Nothing here toggles
  something the card cannot honour, which was the rule that kept them out of the first version.
*/
import { useEffect, useState } from 'react';
import type { Command, NodeDisplay, PptxGeom } from '@integrate/contracts';
import { freshNodeDisplay, freshPptxGeom } from '@integrate/contracts';
import { LiveField } from './live-input.js';
import { Modal } from '../ui/Modal.js';

type PersonKey = keyof NodeDisplay['person'];
type GroupKey = keyof NodeDisplay['group'];
type RootKey = keyof NodeDisplay['root'];

/** Only the toggles the card actually honours — see the header. */
const PERSON_FIELDS: { key: PersonKey; label: string }[] = [
  { key: 'jobTitle', label: 'Job title' },
  { key: 'location', label: 'Location' },
  { key: 'salary', label: 'Salary' },
  { key: 'fte', label: 'FTE (when not 1.0)' },
  { key: 'dedication', label: 'Dedication share' },
  { key: 'stageMarkers', label: 'Stage markers (D1/D2)' },
  { key: 'status', label: 'Carve-out / Remain status' },
  { key: 'rollup', label: 'Subtree size (reporting-line layout only)' },
];

const GROUP_FIELDS: { key: GroupKey; label: string }[] = [
  { key: 'headcount', label: 'Headcount' },
  { key: 'levelTag', label: 'Level name' },
  { key: 'salary', label: 'Total salary' },
  { key: 'fte', label: 'Total FTE' },
  { key: 'headOf', label: 'Designated head' },
  { key: 'compositionBar', label: 'Bucket split bar' },
  { key: 'stageCounts', label: 'Stage comparison line' },
];

const ROOT_FIELDS: { key: RootKey; label: string }[] = [
  { key: 'headcount', label: 'Organisation total' },
  { key: 'headOf', label: 'Organisation head' },
  { key: 'compositionBar', label: 'Bucket split bar' },
  { key: 'stageCounts', label: 'Stage comparison line' },
];

/*
  What the stage-comparison line compares. Both halves have to be non-empty or there is nothing to
  compare — a metric with no stages is a label and no numbers — so the editor says so rather than
  letting the line silently disappear.
*/
const STAGE_LINE_STAGES: { key: keyof NodeDisplay['stageLine']; label: string }[] = [
  { key: 'asis', label: 'As-is' },
  { key: 'p', label: 'Perimeter' },
  { key: 'd1', label: 'Day 1' },
  { key: 'd2', label: 'Day 2' },
  { key: 'r1', label: 'RemainCo after Day 1' },
  { key: 'rs', label: 'RemainCo steady state' },
];

const STAGE_LINE_METRICS: { key: keyof NodeDisplay['stageLine']; label: string }[] = [
  { key: 'hc', label: 'Headcount' },
  { key: 'fte', label: 'FTE' },
  { key: 'cost', label: 'Salary' },
];

const BOX_FIELDS: { key: keyof NodeDisplay['box']; label: string; min: number }[] = [
  { key: 'bw', label: 'Box width (in)', min: 0.1 },
  { key: 'bh', label: 'Box height (in)', min: 0.1 },
  { key: 'hgap', label: 'Horizontal gap (in)', min: 0 },
  { key: 'vgap', label: 'Vertical gap (in)', min: 0 },
];

const BUCKETS: (keyof NodeDisplay['colors']['status'])[] = [
  'Remain',
  'Carve-out',
  'No longer required',
];

export function NodeDisplayEditor({
  nodeDisplay,
  pptxGeom,
  readOnly,
  onDispatch,
  onClose,
}: {
  nodeDisplay: NodeDisplay;
  pptxGeom: PptxGeom;
  readOnly: boolean;
  onDispatch(command: Command): Promise<boolean>;
  onClose(): void;
}): JSX.Element {
  /*
    THE CONFIG AS THIS MODAL BELIEVES IT TO BE: the stored one, plus a change we have sent that has not
    come back yet.

    `setNodeDisplay` is a COMPLETE REPLACEMENT — it sends every key — and each toggle used to build that
    replacement from the `nodeDisplay` prop. The prop only updates when the command's resync lands, so
    two toggles in quick succession built the second from a config that did not yet contain the first,
    and the second silently reverted it. Not a race between people: a race between one person's own two
    clicks, which is the easy one to hit and the hard one to notice.

    Found by the live-editing e2e, which ticks two boxes in a row. It had been recorded as a flake —
    wrongly. Waiting on the redraw rather than sampling it made the failure go from one run in four to
    two in three, which is what said "this never converges" rather than "this is slow".
  */
  const [pending, setPending] = useState<NodeDisplay | null>(null);
  /*
    A NEW prop means the round trip landed — or a colleague wrote one — and either way the stored answer
    is now the truth. Clearing on the prop rather than on our own promise is what keeps a colleague's
    change from being overwritten by a local copy nobody has looked at since.
  */
  useEffect(() => setPending(null), [nodeDisplay]);
  const current = pending ?? nodeDisplay;

  const set = (next: NodeDisplay): void => {
    setPending(next);
    // A refusal never changes the prop, so without this the modal would go on showing an optimistic
    // value the server rejected. `dispatch` resolves either way and says which — see P7b.
    void onDispatch({ type: 'setNodeDisplay', nodeDisplay: next }).then((landed) => {
      if (!landed) setPending(null);
    });
  };

  const toggle = <G extends 'person' | 'group' | 'root'>(group: G, key: string, on: boolean): void =>
    set({ ...current, [group]: { ...current[group], [key]: on } });

  /*
    A blank box restores and dispatches nothing.

    The app commits `Math.max(0, Number(v) || 0)` from an emptied field (`:9724`), which the schema's
    0.1 lower bound would refuse anyway — so clearing a width to retype it would be a rejected command
    and an error banner. Same rule as the assumptions editor: zero has to be typed, and here it cannot
    be typed at all.
  */
  const commitBox = (key: keyof NodeDisplay['box'], raw: string): void => {
    if (raw.trim() === '') return;
    const value = Number(raw);
    if (!Number.isFinite(value) || value === current.box[key]) return;
    set({ ...current, box: { ...current.box, [key]: value } });
  };

  // <input type="color"> speaks '#rrggbb'; `hexColour` wants six digits with no '#'.
  const commitColour = (bucket: keyof NodeDisplay['colors']['status'], raw: string): void => {
    const hex = raw.replace(/^#/, '').toUpperCase();
    if (!/^[0-9A-F]{6}$/.test(hex) || hex === current.colors.status[bucket].toUpperCase()) return;
    set({
      ...current,
      colors: { ...current.colors, status: { ...current.colors.status, [bucket]: hex } },
    });
  };

  const commitGeom = (box: keyof PptxGeom, key: 'x' | 'y' | 'w' | 'h', raw: string): void => {
    if (raw.trim() === '') return;
    const value = Number(raw);
    if (!Number.isFinite(value) || value === pptxGeom[box][key]) return;
    onDispatch({
      type: 'setPptxGeom',
      pptxGeom: { ...pptxGeom, [box]: { ...pptxGeom[box], [key]: value } },
    });
  };

  return (
    <Modal
      testId="node-display-modal"
      title="Card display"
      onClose={onClose}
      footer={
        <>
          {/* One command, so one undo step — the defaults are a state like any other. */}
          <button
            data-testid="nd-reset"
            disabled={readOnly}
            onClick={() => set(freshNodeDisplay())}
          >
            Reset the card display
          </button>
          <button
            data-testid="ppt-geom-reset"
            disabled={readOnly}
            onClick={() => onDispatch({ type: 'setPptxGeom', pptxGeom: freshPptxGeom() })}
          >
            Reset the slide geometry
          </button>
          <span className="spacer" />
          <button className="primary" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      <p className="muted" style={{ marginTop: 0 }}>
        Shared with everybody on the project, and read by the deck exporter — a slide shows what the
        screen shows, which only holds if there is one answer. Each change is its own entry in the
        history and its own undo step.
      </p>

      <h5 className="asm-grid-h" style={{ marginTop: 0 }}>
        Person cards
      </h5>
      {PERSON_FIELDS.map((f) => (
        <label className="check" key={f.key}>
          <input
            type="checkbox"
            data-testid={`nd-person-${f.key}`}
            checked={current.person[f.key]}
            disabled={readOnly}
            onChange={(e) => toggle('person', f.key, e.target.checked)}
          />
          <span>{f.label}</span>
        </label>
      ))}

      <h5 className="asm-grid-h">Group cards</h5>
      {GROUP_FIELDS.map((f) => (
        <label className="check" key={f.key}>
          <input
            type="checkbox"
            data-testid={`nd-group-${f.key}`}
            checked={current.group[f.key]}
            disabled={readOnly}
            onChange={(e) => toggle('group', f.key, e.target.checked)}
          />
          <span>{f.label}</span>
        </label>
      ))}

      <h5 className="asm-grid-h">The organisation card</h5>
      {ROOT_FIELDS.map((f) => (
        <label className="check" key={f.key}>
          <input
            type="checkbox"
            data-testid={`nd-root-${f.key}`}
            checked={current.root[f.key]}
            disabled={readOnly}
            onChange={(e) => toggle('root', f.key, e.target.checked)}
          />
          <span>{f.label}</span>
        </label>
      ))}

      {(current.group.stageCounts || current.root.stageCounts) && (
        <>
          <h5 className="asm-grid-h">What the stage comparison compares</h5>
          <p className="muted asm-help" style={{ marginTop: 0 }}>
            One line per metric, comparing the stages you pick. It deliberately ignores the stage
            lens and respects the filters, which is why it can disagree with the headline above it:
            one is the stage you are looking at, the other is every stage at once. Computing it is a
            second pass over the roster, so it runs only while this is on.
          </p>
          <div className="nd-columns">
            <div>
              {STAGE_LINE_STAGES.map((f) => (
                <label className="check" key={f.key}>
                  <input
                    type="checkbox"
                    data-testid={`nd-stage-${f.key}`}
                    checked={current.stageLine[f.key]}
                    disabled={readOnly}
                    onChange={(e) =>
                      set({
                        ...current,
                        stageLine: { ...current.stageLine, [f.key]: e.target.checked },
                      })
                    }
                  />
                  <span>{f.label}</span>
                </label>
              ))}
            </div>
            <div>
              {STAGE_LINE_METRICS.map((f) => (
                <label className="check" key={f.key}>
                  <input
                    type="checkbox"
                    data-testid={`nd-stage-${f.key}`}
                    checked={current.stageLine[f.key]}
                    disabled={readOnly}
                    onChange={(e) =>
                      set({
                        ...current,
                        stageLine: { ...current.stageLine, [f.key]: e.target.checked },
                      })
                    }
                  />
                  <span>{f.label}</span>
                </label>
              ))}
            </div>
          </div>
          {!STAGE_LINE_STAGES.some((f) => current.stageLine[f.key]) && (
            <p className="muted asm-help" data-testid="nd-stage-empty">
              No stages picked, so no line renders. Pick at least one stage and one metric.
            </p>
          )}
          {!STAGE_LINE_METRICS.some((f) => current.stageLine[f.key]) && (
            <p className="muted asm-help" data-testid="nd-metric-empty">
              No metrics picked, so no line renders. Pick at least one stage and one metric.
            </p>
          )}
        </>
      )}

      <h5 className="asm-grid-h">Deck colours</h5>
      <p className="muted asm-help" style={{ marginTop: 0 }}>
        PowerPoint only. On screen the buckets are drawn from the stylesheet&rsquo;s own palette,
        which the deck cannot reproduce — so these change the slides and not the chart.
      </p>
      {BUCKETS.map((bucket) => (
        <div className="sp-row" key={bucket}>
          <span className="k">{bucket}</span>
          <span className="v">
            <input
              type="color"
              data-testid={`nd-color-status-${bucket.replace(/\s+/g, '-')}`}
              value={`#${current.colors.status[bucket]}`}
              disabled={readOnly}
              onChange={(e) => commitColour(bucket, e.target.value)}
            />
          </span>
        </div>
      ))}

      <h5 className="asm-grid-h">Deck box geometry</h5>
      <p className="muted asm-help" style={{ marginTop: 0 }}>
        PowerPoint only, in inches. The chart lays itself out; these size the boxes on a slide.
      </p>
      <div className="field-grid">
        {BOX_FIELDS.map((f) => (
          <div className="field" key={f.key}>
            <label>{f.label}</label>
            <LiveField
              type="number"
              data-testid={`nd-box-${f.key}`}
              value={String(current.box[f.key])}
              min={f.min}
              max={20}
              step={0.1}
              disabled={readOnly}
              onCommit={(text) => commitBox(f.key, text)}
            />
          </div>
        ))}
      </div>

      <h5 className="asm-grid-h">Slide geometry</h5>
      <p className="muted asm-help" style={{ marginTop: 0 }}>
        Where the title and the content sit on every slide, in centimetres. Its own command, because
        it is a different decision from what a card shows.
      </p>
      {(['title', 'content'] as const).map((box) => (
        <div className="field-grid" key={box}>
          {(['x', 'y', 'w', 'h'] as const).map((axis) => (
            <div className="field" key={axis}>
              <label>
                {box} {axis}
              </label>
              <LiveField
                type="number"
                data-testid={`ppt-geom-${box}-${axis}`}
                value={String(pptxGeom[box][axis])}
                min={0}
                step={0.1}
                disabled={readOnly}
                onCommit={(text) => commitGeom(box, axis, text)}
              />
            </div>
          ))}
        </div>
      ))}
    </Modal>
  );
}
